import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, Lock, Truck, MessageCircle, Loader2, CreditCard, MapPin, Check, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { PAYPAL_OPTIONS, PayIn4, KlarnaPay3 } from "@/components/PayIn4";
import { PROMO_CODE, PROMO_PERCENT, usePromo } from "@/lib/promo";
import { fmt } from "@/data/packs";
import { getRef } from "@/lib/ref";
import { MondialRelayPicker } from "@/components/MondialRelayPicker";
import { BuyerTerms, isBuyerValid, buyerPayload } from "@/components/BuyerTerms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIPPING = [
  { key: "mondial_relay", label: "Mondial Relay — Point relais", price: 0, badge: "Gratuit" },
  { key: "colissimo_reco", label: "Colissimo recommandé (France métro.)", price: 13.9 },
  { key: "colissimo_domtom", label: "Colissimo DOM-TOM", price: 11.9 },
];

export const CartDrawer = () => {
  const { open, setOpen, detailed, total, discount, totalAfter, count, setQty, removeItem,
          promo, applyPromo, clearPromo, clearAll } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { loaded: promoLoaded, active: promoActive } = usePromo();
  const [promoInput, setPromoInput] = useState("");
  useEffect(() => {
    if (!promoLoaded) return;
    if (promoActive) setPromoInput((v) => v || PROMO_CODE);
    else if (promo?.code === PROMO_CODE) clearPromo();
  }, [promoLoaded, promoActive]); // eslint-disable-line react-hooks/exhaustive-deps
  const [promoMsg, setPromoMsg] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [config, setConfig] = useState({ paypal_enabled: false, paypal_client_id: "", paypal_mode: "sandbox", cic_enabled: false });
  const [method, setMethod] = useState("card");
  const [shipMethod, setShipMethod] = useState("mondial_relay");
  const [relay, setRelay] = useState(null);
  const [buyer, setBuyer] = useState({ type: "particulier", company: "", siret: "", cgv: false });

  const shipCost = (SHIPPING.find((s) => s.key === shipMethod) || {}).price || 0;
  const grandTotal = Math.round((totalAfter + shipCost) * 100) / 100;
  const needsRelay = shipMethod === "mondial_relay" && !relay;
  const buyerOk = isBuyerValid(buyer);
  const blocked = needsRelay || !buyerOk;
  const blockMsg = needsRelay
    ? "Sélectionnez un point relais Mondial Relay pour continuer."
    : !buyer.cgv
      ? "Veuillez accepter les CGV pour continuer."
      : "Renseignez votre raison sociale et votre SIRET (14 chiffres).";

  useEffect(() => {
    axios.get(`${API}/config/payments`).then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const validatePromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoMsg("");
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code });
      if (data.valid) {
        applyPromo(data.code, data.percent);
        setPromoMsg(`Code ${data.code} appliqué : -${data.percent}%`);
        setPromoInput("");
      } else {
        setPromoMsg("Code promo invalide.");
      }
    } catch {
      setPromoMsg("Impossible de vérifier le code.");
    }
    setPromoLoading(false);
  };

  const checkout = async () => {
    if (blocked) {
      setError(blockMsg);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const items = detailed.map((i) => ({ lookup_key: i.id, quantity: i.qty }));
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items,
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
        ref_code: getRef(),
        shipping_method: shipMethod,
        relay_point: shipMethod === "mondial_relay" ? relay : null,
        ...buyerPayload(buyer),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError("Une erreur est survenue lors de la préparation du paiement. Merci de réessayer.");
      setLoading(false);
    }
  };

  const paypalItems = () => detailed.map((i) => ({ lookup_key: i.id, quantity: i.qty }));

  const payWithCic = async () => {
    if (blocked) {
      setError(blockMsg);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const items = detailed.map((i) => ({ lookup_key: i.id, quantity: i.qty }));
      const { data } = await axios.post(`${API}/cic/prepare`, {
        items,
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
        ref_code: getRef(),
        shipping_method: shipMethod,
        relay_point: shipMethod === "mondial_relay" ? relay : null,
        ...buyerPayload(buyer),
      });
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.payment_url;
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v ?? "";
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setError("Une erreur est survenue lors de la préparation du paiement CIC. Merci de réessayer.");
      setLoading(false);
    }
  };

  const createPaypalOrder = async () => {
    setError("");
    const { data } = await axios.post(`${API}/paypal/create-order`, {
      items: paypalItems(),
      promo_code: promo?.code || null,
      ref_code: getRef(),
      shipping_method: shipMethod,
      relay_point: shipMethod === "mondial_relay" ? relay : null,
      ...buyerPayload(buyer),
    });
    return data.id;
  };

  const onPaypalApprove = async (data) => {
    try {
      const { data: res } = await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
      if (res.payment_status === "paid") {
        clearAll();
        setOpen(false);
        navigate(`/payment/success?order_number=${res.order_number}`);
      } else {
        setError("Le paiement PayPal n'a pas pu être confirmé. Merci de réessayer.");
      }
    } catch (e) {
      setError("Une erreur est survenue lors du paiement PayPal. Merci de réessayer.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="w-full sm:max-w-md flex flex-col p-0 bg-[var(--oz-bg)]"
      >
        <SheetHeader className="px-6 py-5 border-b border-[var(--oz-border)] bg-white">
          <SheetTitle className="font-serif text-2xl flex items-center gap-2 text-[var(--oz-text)]">
            <ShoppingBag className="w-5 h-5 text-[var(--oz-gold)]" /> Votre panier
          </SheetTitle>
          <SheetDescription className="sr-only">
            Récapitulatif de votre panier et passage à la commande
          </SheetDescription>
        </SheetHeader>

        {count === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <div className="w-16 h-16 rounded-full bg-[var(--oz-nude)] flex items-center justify-center text-[var(--oz-badge)]">
              <ShoppingBag className="w-7 h-7" />
            </div>
            <p className="text-[var(--oz-muted)]">Votre panier est vide.</p>
            <button
              onClick={() => setOpen(false)}
              className="text-sm font-semibold text-[var(--oz-gold)] hover:underline"
              data-testid="cart-continue-shopping"
            >
              Découvrir les packs
            </button>
          </div>
        ) : (
          <div data-testid="cart-scroll" className="flex-1 min-h-0 overflow-y-auto">
            <div className="px-6 py-5 space-y-4">
              {detailed.map((i) => (
                <div
                  key={i.id}
                  data-testid={`cart-item-${i.id}`}
                  className="flex gap-4 bg-white rounded-2xl p-3 border border-[var(--oz-border)]"
                >
                  <img src={i.pack.image} alt={i.pack.name} className="w-20 h-20 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div>
                        <h4 className="font-serif text-base text-[var(--oz-text)]">{i.pack.name}</h4>
                        <p className="text-xs text-[var(--oz-muted)]">{i.pack.products} produits</p>
                      </div>
                      <button
                        data-testid={`cart-remove-${i.id}`}
                        onClick={() => removeItem(i.id)}
                        className="text-[var(--oz-muted)] hover:text-[var(--oz-badge)] transition-colors h-fit"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 border border-[var(--oz-border)] rounded-full">
                        <button
                          data-testid={`cart-dec-${i.id}`}
                          onClick={() => setQty(i.id, i.qty - 1)}
                          className="p-1.5 hover:text-[var(--oz-gold)]"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm font-semibold">{i.qty}</span>
                        <button
                          data-testid={`cart-inc-${i.id}`}
                          onClick={() => setQty(i.id, i.qty + 1)}
                          className="p-1.5 hover:text-[var(--oz-gold)]"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="font-semibold text-[var(--oz-text)]">{fmt(i.pack.price * i.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex flex-wrap gap-3 pt-2 text-xs text-[var(--oz-muted)]">
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[var(--oz-gold)]" /> Paiement sécurisé</span>
                <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-[var(--oz-gold)]" /> Livraison suivie</span>
                <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-[var(--oz-gold)]" /> Support client</span>
              </div>
            </div>

            <div className="border-t border-[var(--oz-border)] bg-white px-6 py-5 space-y-3">
              {!promo && promoActive && (
                <div data-testid="cart-promo-reminder" className="flex items-center gap-2 rounded-xl bg-[var(--oz-gold)] text-[var(--oz-dark)] px-3 py-2 text-xs font-semibold">
                  <Tag className="w-4 h-4 shrink-0" />
                  <span className="flex-1">-{PROMO_PERCENT} % offerts avec le code {PROMO_CODE} : cliquez sur « Appliquer »</span>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  data-testid="promo-input"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  placeholder="Code promo"
                  className="flex-1 rounded-full border border-[var(--oz-border)] px-4 py-2.5 text-sm outline-none focus:border-[var(--oz-gold)]"
                />
                <button
                  data-testid="promo-apply"
                  onClick={validatePromo}
                  disabled={promoLoading}
                  className="px-4 py-2.5 rounded-full border border-[var(--oz-dark)] text-sm font-semibold hover:bg-[var(--oz-dark)] hover:text-white transition-colors disabled:opacity-60"
                >
                  {promoLoading ? "..." : "Appliquer"}
                </button>
              </div>
              {promoMsg && <p className="text-xs text-[var(--oz-muted)]">{promoMsg}</p>}

              <div className="flex justify-between text-sm text-[var(--oz-muted)]">
                <span>Sous-total</span>
                <span data-testid="cart-subtotal">{fmt(total)}</span>
              </div>
              {promo && discount > 0 && (
                <div className="flex justify-between text-sm font-semibold text-[#8A6A2E]">
                  <span className="inline-flex items-center gap-1">
                    Réduction ({promo.code})
                    <button data-testid="promo-remove" onClick={() => { clearPromo(); setPromoInput(promoActive ? PROMO_CODE : ""); setPromoMsg(""); }} className="underline text-[var(--oz-muted)]">retirer</button>
                  </span>
                  <span data-testid="cart-discount">- {fmt(discount)}</span>
                </div>
              )}
              <div className="pt-1" data-testid="shipping-selector">
                <p className="text-sm font-semibold text-[var(--oz-text)] mb-2">Mode de livraison</p>
                <div className="space-y-2">
                  {SHIPPING.map((s) => (
                    <label
                      key={s.key}
                      data-testid={`ship-${s.key}`}
                      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                        shipMethod === s.key
                          ? "border-[var(--oz-gold)] bg-[var(--oz-nude)]/40"
                          : "border-[var(--oz-border)] hover:border-[var(--oz-gold)]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="shipping"
                        className="accent-[var(--oz-gold)]"
                        checked={shipMethod === s.key}
                        onChange={() => { setShipMethod(s.key); setError(""); }}
                      />
                      <span className="flex-1 text-sm text-[var(--oz-text)]">{s.label}</span>
                      <span className="text-sm font-semibold text-[var(--oz-text)]">
                        {s.price === 0 ? <span className="text-[var(--oz-gold)]">Gratuit</span> : fmt(s.price)}
                      </span>
                    </label>
                  ))}
                </div>

                {shipMethod === "mondial_relay" && (
                  <div className="mt-2">
                    {relay ? (
                      <div data-testid="relay-selected" className="flex items-start gap-2 rounded-xl border border-[var(--oz-gold)] bg-[var(--oz-nude)]/40 px-3 py-2.5">
                        <MapPin className="w-4 h-4 text-[var(--oz-gold)] mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[var(--oz-text)]">{relay.name}</p>
                          <p className="text-xs text-[var(--oz-muted)]">{relay.address}</p>
                        </div>
                        <button
                          data-testid="relay-change"
                          onClick={() => setRelay(null)}
                          className="text-xs font-semibold text-[var(--oz-gold)] underline flex-shrink-0"
                        >
                          Changer
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-[var(--oz-muted)] mb-1 inline-flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-[var(--oz-gold)]" /> Choisissez votre point relais :
                        </p>
                        <MondialRelayPicker onSelect={setRelay} />
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-between text-sm text-[var(--oz-muted)]">
                <span>Livraison</span>
                <span data-testid="cart-shipping">{shipCost === 0 ? <span className="text-[var(--oz-gold)] font-semibold">Offerte</span> : fmt(shipCost)}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold text-[var(--oz-text)] pt-2 border-t border-[var(--oz-border)]">
                <span>Total</span>
                <span data-testid="cart-total">{fmt(grandTotal)}</span>
              </div>

              <BuyerTerms buyer={buyer} onChange={(b) => { setBuyer(b); setError(""); }} onNavigate={() => setOpen(false)} />

              {(config.paypal_enabled || config.cic_enabled) && (
                <div className="pt-1" data-testid="payment-method-selector">
                  <p className="text-sm font-semibold text-[var(--oz-text)] mb-2">Choisissez votre moyen de paiement</p>
                  <div className="flex flex-col gap-2">
                    <button
                      data-testid="method-card"
                      onClick={() => setMethod("card")}
                      className={`w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                        method === "card"
                          ? "border-[var(--oz-dark)] bg-[var(--oz-dark)] text-white"
                          : "border-[var(--oz-border)] text-[var(--oz-muted)] hover:border-[var(--oz-dark)]"
                      }`}
                    >
                      <CreditCard className="w-4 h-4" /> Carte bancaire — Paiement sécurisé {config.cic_enabled ? "" : "(Stripe)"}
                    </button>
                    {config.cic_enabled && (
                      <button
                        data-testid="method-cic"
                        onClick={() => setMethod("cic")}
                        className={`w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                          method === "cic"
                            ? "border-[var(--oz-dark)] bg-[var(--oz-dark)] text-white"
                            : "border-[var(--oz-border)] text-[var(--oz-muted)] hover:border-[var(--oz-dark)]"
                        }`}
                      >
                        <CreditCard className="w-4 h-4" /> Carte bancaire — CIC Paiement
                      </button>
                    )}
                    {config.paypal_enabled && (
                      <button
                        data-testid="method-paypal"
                        onClick={() => setMethod("paypal")}
                        className={`w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                          method === "paypal"
                            ? "border-[#003087] bg-[#003087] text-white"
                            : "border-[var(--oz-border)] text-[var(--oz-muted)] hover:border-[#003087]"
                        }`}
                      >
                        PayPal — Payer avec PayPal ou en 4X sans frais
                      </button>
                    )}
                  </div>
                </div>
              )}

              {error && <p className="text-xs text-[var(--oz-badge)]">{error}</p>}

              {method === "card" && (
                <>
                  <button
                    data-testid="checkout-submit-button"
                    onClick={checkout}
                    disabled={loading || blocked}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm tracking-wide hover:bg-black transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Redirection...</>
                    ) : (
                      <><Lock className="w-4 h-4" /> COMMANDER — {fmt(grandTotal)}</>
                    )}
                  </button>
                  {blocked && (
                    <p data-testid="checkout-blocked-hint" className="text-[11px] text-center text-[var(--oz-badge)]">
                      {blockMsg}
                    </p>
                  )}
                  <div className="flex justify-center"><KlarnaPay3 amount={grandTotal} testid="cart-klarna3" /></div>
                  <p className="text-[11px] text-center text-[var(--oz-muted)]">
                    Paiement sécurisé par carte bancaire via Stripe. Pas de PayPal ? Choisissez « Klarna » sur la page de paiement pour régler en 3X.
                  </p>
                </>
              )}

              {method === "cic" && config.cic_enabled && (
                <>
                  <button
                    data-testid="checkout-cic-button"
                    onClick={payWithCic}
                    disabled={loading || blocked}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm tracking-wide hover:bg-black transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Redirection...</>
                    ) : (
                      <><Lock className="w-4 h-4" /> PAYER PAR CARTE — {fmt(grandTotal)}</>
                    )}
                  </button>
                  {blocked && (
                    <p className="text-[11px] text-center text-[var(--oz-badge)]">{blockMsg}</p>
                  )}
                  <p className="text-[11px] text-center text-[var(--oz-muted)]">
                    Paiement sécurisé par carte bancaire via CIC Paiement.
                  </p>
                </>
              )}

              {method === "paypal" && config.paypal_enabled && (
                <div data-testid="paypal-buttons">
                  {blocked ? (
                    <p data-testid="paypal-blocked-hint" className="text-[11px] text-center text-[var(--oz-badge)] py-2">
                      {blockMsg}
                    </p>
                  ) : (
                    <PayPalScriptProvider
                      options={PAYPAL_OPTIONS(config.paypal_client_id)}
                    >
                      <PayPalButtons
                        style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
                        forceReRender={[totalAfter, promo?.code, shipMethod, buyer.type, buyer.company, buyer.siret]}
                        createOrder={createPaypalOrder}
                        onApprove={onPaypalApprove}
                        onError={() => setError("Une erreur est survenue avec PayPal. Merci de réessayer.")}
                      />
                    </PayPalScriptProvider>
                  )}
                  <div className="flex justify-center"><PayIn4 amount={grandTotal} testid="cart-pay4" /></div>
                  <p className="text-[11px] text-center text-[var(--oz-muted)]">
                    Paiement sécurisé via PayPal. Choisissez « Payer en 4X » dans la fenêtre PayPal.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
